import { debounce } from 'lodash';
import * as React from 'react';
import { MutableRefObject, useRef } from 'react';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { WithTranslation, withTranslation } from 'react-i18next';
import {
    FieldConfig,
    FormArray,
    FormGenerator,
    FormGroup
} from 'react-reactive-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { RouteComponentProps } from 'react-router-dom';
import ReactTable, { Column, FinalState, RowInfo } from 'react-table';
import selectTableHOC, {
    SelectTableAdditionalProps
} from 'react-table/lib/hoc/selectTable';
import treeTableHOC from 'react-table/lib/hoc/treeTable';
import * as types from '../../actions/actionTypes';
import { beginAjaxCall, endAjaxCall } from '../../actions/ajaxStatusActions';
import {
    FacilitiesSearchParams,
    closeAllModals,
    getFacilitiesByCountry,
    searchFacilitiesForAsyncSelect,
    toggleEditFacilityModal
} from '../../actions/commonActions';
import { loadCountries } from '../../actions/countriesActions';
import {
    assignStandardAPI,
    mergeFacilitiesAPI,
    setSelectedFacilityID,
    setTableFilter
} from '../../actions/manageFacilityActions';
import { getProductInfo } from '../../actions/manageInventoryActions';
import API from '../../constants/apiEndpoints';
import { constants } from '../../constants/constants';
import {
    capWords,
    formatFacilityStreet,
    getFacilityDisplayName
} from '../../helpers/stringHelpers';
import { Ifacility, Ioption, ItableFiltersReducer, Itile } from '../../models';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import { getCountries } from '../../reducers/countriesReducer';
import { selectFilteredFacilities } from '../../reducers/facilitiesReducer';
import { emptyTile, initialFacility } from '../../reducers/initialState';
import { selectManageFacilityTableFilters } from '../../reducers/manageCustomerAndFacilityReducer';
import { getProductInfo as getProductInfoSelector } from '../../reducers/productInfoReducer';
import { getUsersCountries } from '../../reducers/userReducer';
import { AddButton } from '../common/AddButton';
import { Banner } from '../common/Banner';
import ExportToExcelButton, {
    ExportRequest
} from '../common/ExportToExcelButton';
import EditFacilityModal from '../common/EditFacilityModal';
import FacilityContactModal from '../common/FacilityContactModal';
import FacilityContractModal from '../common/FacilityContractModal';
import { FormUtil } from '../common/FormUtil';
import AssignStandardModal from './assignStandardModal';
import MergeFacilitiesModal from './mergeFacilitiesModal';

interface RowInfoFacility extends RowInfo {
    original: Ifacility;
}

type Iprops = RouteComponentProps<any>;

const SelectTable = selectTableHOC(treeTableHOC(ReactTable));

interface IdispatchProps {
    totalPages: number;
    setTableFilter: typeof setTableFilter;
    tableFilters: ItableFiltersReducer;
    loading: boolean;
    setSelectedFacilityID: typeof setSelectedFacilityID;
    toggleEditFacilityModal: typeof toggleEditFacilityModal;
    facilities: { [key: string]: Ifacility };
    getProductInfo: typeof getProductInfo;
    c: number;
    getFacilitiesByCountry: typeof getFacilitiesByCountry;
    filteredFacilities: Ifacility[];
    closeAllModals: typeof closeAllModals;
    searchFacilitiesForAsyncSelect: typeof searchFacilitiesForAsyncSelect;
    beginAjaxCall: typeof beginAjaxCall;
    endAjaxCall: typeof endAjaxCall;
}

interface Istate {
    selectedRow: any;
    selectAll: boolean;
    tableData: Ifacility[];
    currentTile: Itile;
    filter: FacilitiesSearchParams;
    selectedFacility: Ifacility;
    checkboxTable: any;
    selection: string[];
    showMergeFacilityModal: boolean;
    showAssignStandardModal: boolean;
    searchText: string;
}

const ManageFacility: React.FC<Iprops &
    IdispatchProps &
    SelectTableAdditionalProps &
    WithTranslation &
    Istate> = props => {
    const { t } = props;
    const dispatch = useDispatch();
    const countries = useSelector(getCountries);
    const usersCountries = useSelector(getUsersCountries);
    const productInfo = useSelector(getProductInfoSelector);
    const [config, setConfig] = React.useState<FieldConfig>();
    const [state, setState] = React.useState<Istate>({
        selectedRow: {},
        selectAll: false,
        tableData: [],
        currentTile: emptyTile,
        filter: {
            name: '',
            address: '',
            postalCode: ''
        },
        selectedFacility: initialFacility,
        checkboxTable: {},
        selection: [],
        showMergeFacilityModal: false,
        showAssignStandardModal: false,
        searchText: ''
    });
    const [data, setData] = React.useState<{
        result: any[];
        count: number;
        pages: number;
    }>({ result: [], count: 0, pages: 0 });
    const [formValues, setFormValues] = React.useState<{
        country: Ioption | null;
        facilityName: string | undefined;
        quantityOfAssets: number | undefined;
        standard: Ioption[] | null;
    }>({
        country: null,
        quantityOfAssets: undefined,
        facilityName: undefined,
        standard: null
    });

    // The Standard filter is a multi-select, so the form value is an array of
    // options (or null once cleared). Everything downstream wants plain IDs.
    const standardIDsOf = (standard: Ioption[] | null | undefined): string[] =>
        standard ? standard.map(option => option.value) : [];
    const ref = useRef() as MutableRefObject<HTMLInputElement>;

    const saveFacilities = (facilities: Ifacility[]) => {
        setState({ ...state, tableData: facilities });
        props.endAjaxCall();
    };

    const debouncedSearch = React.useRef(
        debounce(
            async (
                searchTerms,
                countries,
                quantityOfAssets,
                page,
                standardIDs
            ) => {
                props.beginAjaxCall();
                props.searchFacilitiesForAsyncSelect(
                    searchTerms,
                    countries !== undefined ? countries : usersCountries,
                    quantityOfAssets !== '' ? quantityOfAssets : null,
                    () => {
                        return;
                    },
                    saveFacilities,
                    page + 1,
                    (_data: any) => {
                        setData(_data);
                    },
                    standardIDs
                );
            },
            constants.formSearchDebounceTime
        )
    ).current;

    const refreshData = () => {
        const {
            country,
            quantityOfAssets,
            facilityName,
            standard
        } = formValues;

        debouncedSearch(
            facilityName && facilityName.length >= 3 ? facilityName : undefined,
            country?.value !== undefined ? country.value : usersCountries,
            quantityOfAssets !== undefined ? quantityOfAssets : null,
            props.tableFilters.page,
            standardIDsOf(standard)
        );
    };

    React.useEffect(() => {
        loadCountries(dispatch, countries);
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        // Check if productInfo has been loaded, if not, load it
        if (
            !productInfo.standards ||
            Object.keys(productInfo.standards).length === 0
        ) {
            dispatch(getProductInfo());
        }
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        const countriesOptions: Ioption[] = countries
            .filter(c => usersCountries?.includes(c.id))
            .map(c => ({ label: c.name, value: c.id }));
        // Order is Facility, Country, Standard, Quantity of Assets at 1/4 each
        // so the four filters read consistently and fit one row (MMG-4352).
        const mainSearchControls = {
            facilityName: {
                render: FormUtil.TextInputWithoutValidation,
                meta: {
                    label: 'common:facility',
                    colWidth: 3,
                    type: 'text',
                    placeholder: 'common:facilitySearchPlaceholderDetail',
                    defaultValue: '',
                    isClearable: true
                }
            },
            country: {
                render: FormUtil.Select,
                meta: {
                    options: countriesOptions,
                    label: 'manageFacility:Country',
                    colWidth: 3,
                    colWidthMedium: 3,
                    colWidthLarge: 3,
                    placeholder: 'common:searchPlaceholder',
                    name: 'country',
                    defaultValue: '',
                    isClearable: true
                }
            },
            standard: {
                render: FormUtil.MultiSelectFilter,
                meta: {
                    options: productInfo.standardOptions,
                    label: 'common:standard',
                    colWidth: 3,
                    colWidthMedium: 3,
                    colWidthLarge: 3,
                    name: 'standard',
                    defaultValue: '',
                    t: props.t
                }
            },
            quantityOfAssets: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'manageFacility:quantityOfAssets',
                    colWidth: 3,
                    type: 'number',
                    placeholder: 'manageFacility:quantityOfAssets',
                    defaultValue: '',
                    isClearable: true
                }
            }
        };

        const searchFieldConfig = {
            controls: { ...mainSearchControls }
        } as FieldConfig;
        setConfig(FormUtil.translateForm(searchFieldConfig, props.t));
        // standardOptions arrive with the productInfo call above, after the
        // first render - rebuild once they land or the filter has no options.
        // eslint-disable-next-line
    }, [countries, usersCountries, productInfo.standardOptions]);

    const colorButton =
        constants.colors[
            `${state.currentTile.color}Button` as keyof typeof constants.colors
        ];

    // countryID -> SAP code, for the DE/AT name concatenation (MMG-4192).
    const countryCodeById = React.useMemo(() => {
        const map: { [id: string]: string | undefined } = {};
        countries.forEach(c => (map[c.id] = c.sapCountryCode));
        return map;
    }, [countries]);

    const columns: any[] = [
        {
            Header: t('user:name'),
            id: 'name',
            accessor: (f: Ifacility) =>
                getFacilityDisplayName(
                    f.name,
                    f.name2,
                    countryCodeById[f.countryID]
                ),
            minWidth: 200
        },
        {
            Header: t('facility:facilityForm.sapFacilityNumber'),
            id: 'sapFacilityNumber',
            accessor: 'sapFacilityNumber',
            minWidth: 150
        },
        {
            Header: t('manageFacility:quantityOfAssets'),
            id: 'quantityOfAssets',
            accessor: 'quantityOfAssets',
            minwidth: 70
        },
        {
            Header: t('common:country'),
            id: 'country',
            accessor: ({ countryID }: Ifacility) => {
                const country = constants.countries[countryID];
                return country ? country.label : '';
            },
            minWidth: 120
        },
        {
            Header: t('user:address'),
            id: 'address',
            accessor: ({
                address,
                address2,
                city,
                state: facilityState,
                postalCode,
                countryID
            }: Ifacility) => {
                const street = formatFacilityStreet(
                    address,
                    address2,
                    countryID
                );
                return (
                    `${street},` +
                    ` ${city ? city : ''},` +
                    ` ${facilityState ? facilityState : ''},` +
                    ` ${postalCode ? postalCode : ''}`
                );
            },
            minWidth: 300
        }
    ];

    /**
     * Toggle all checkboxes for select table
     */
    const toggleAll = () => {
        const { keyField = 'id' } = props;
        const selectAll = !state.selectAll;
        const selection: string[] = [];

        if (selectAll && keyField !== undefined) {
            // we need to get at the internals of ReactTable
            const wrappedInstance = state.checkboxTable.getWrappedInstance();
            // the 'sortedData' property contains the currently accessible records based on the filter and sort
            const currentRecords: any[] = wrappedInstance.getResolvedState()
                .sortedData;
            // we just push all the IDs onto the selection array
            currentRecords.forEach(item => {
                selection.push(`select-${item._original[keyField]}`);
            });
        }

        setState({ ...state, selectAll, selection });
    };

    /**
     * Whether or not a row is selected for select table
     */
    const isSelected = (key: string) => {
        return state.selection.includes(`select-${key}`);
    };

    const toggleSelection = (key: string, shift: boolean, row: any) => {
        // start off with the existing state
        let selection = [...state.selection];
        const keyIndex = selection.indexOf(key);

        // check to see if the key exists
        if (keyIndex >= 0) {
            // it does exist so we will remove it using destructing
            selection = [
                ...selection.slice(0, keyIndex),
                ...selection.slice(keyIndex + 1)
            ];
        } else {
            // it does not exist so add it
            selection.push(key);
        }

        setState({ ...state, selection });
    };

    // Since this page does not automatically save facilities into Redux
    // Soon as they take an action against a facility (select or try to merge)
    // We need to save it so other screens work correctly. Since everything usually
    // looks up a facility by ID in redux
    const saveFacility = () => {
        let facilitiesToSave: Ifacility[] = [];

        state.selection.forEach(selection => {
            const id = selection.slice(7);
            const tempFacility = state.tableData.find(x => x.id === id);
            if (tempFacility !== undefined) {
                facilitiesToSave.push(tempFacility);
            }
        });

        if (facilitiesToSave.length > 0) {
            dispatch({
                type: types.GET_FACILITIES_SUCCESS,
                facilities: facilitiesToSave
            });
        }
    };

    const getTdProps = (
        finalState: FinalState,
        rowInfo: RowInfoFacility | undefined,
        column: Column | undefined,
        instance: any
    ) => {
        if (rowInfo) {
            return {
                onClick: () => {
                    saveFacility();
                    props.setSelectedFacilityID(rowInfo.original.id);
                    setState({ ...state, selectedFacility: rowInfo.original });
                    props.toggleEditFacilityModal();
                }
            };
        }
        return {};
    };

    const onPageChange = (page: number) => {
        props.setTableFilter({ page });
        setState({ ...state, selectedRow: {} });

        if (formValues) {
            const {
                country,
                quantityOfAssets,
                facilityName,
                standard
            } = formValues;
            props.beginAjaxCall();
            if (facilityName === undefined || facilityName.length === 0) {
                props.searchFacilitiesForAsyncSelect(
                    undefined,
                    country?.value !== undefined
                        ? country.value
                        : usersCountries,
                    quantityOfAssets !== undefined ? quantityOfAssets : null,
                    () => {
                        return;
                    },
                    saveFacilities,
                    page + 1,
                    (data: any) => {
                        setData(data);
                    },
                    standardIDsOf(standard)
                );
                return;
            } else if (
                formValues.facilityName &&
                formValues.facilityName.length >= 3
            ) {
                props.searchFacilitiesForAsyncSelect(
                    facilityName,
                    country?.value !== undefined
                        ? country.value
                        : usersCountries,
                    quantityOfAssets !== undefined ? quantityOfAssets : null,
                    () => {
                        return;
                    },
                    saveFacilities,
                    page + 1,
                    (data: any) => {
                        setData(data);
                    },
                    standardIDsOf(standard)
                );
                return;
            }
        }
    };

    const onPageSizeChange = (rows: number) => {
        props.setTableFilter({ rows, page: 0 });
        setState({ ...state, selectedRow: {} });
    };

    const addFacility = (row: any) => {
        props.setSelectedFacilityID(initialFacility.id);
        setState({ ...state, selectedFacility: initialFacility });
        props.toggleEditFacilityModal();
    };

    /*
     * Builds the CSV export request at click time so it reflects the current
     * country / name / quantity filters - the same inputs the grid search
     * uses. Column headers are the grid's translated labels; Country is
     * resolved via the same constants.countries map the grid accessor uses.
     */
    const buildExportRequest = (): ExportRequest => {
        const { t } = props;
        const {
            country,
            quantityOfAssets,
            facilityName,
            standard
        } = formValues;
        const countries =
            country && country.value !== undefined && country.value !== ''
                ? [country.value]
                : usersCountries;
        const search =
            facilityName && facilityName.length >= 3 ? facilityName : undefined;

        const countryMap: { [id: string]: string } = {};
        Object.keys(constants.countries).forEach(id => {
            countryMap[id] = constants.countries[id].label;
        });

        return {
            url: API.GET.facility.exportCsv,
            body: {
                countries,
                search,
                // Coerce to a real number: the form value is a string and the
                // API binds this from the JSON body as int? (a JSON string
                // would silently fail to bind -> filter skipped -> every
                // facility exported). The grid works because its request
                // sends this as a query-string param instead.
                quantityOfAssets:
                    quantityOfAssets !== undefined &&
                    quantityOfAssets !== null &&
                    `${quantityOfAssets}`.trim() !== '' &&
                    !Number.isNaN(Number(quantityOfAssets))
                        ? Number(quantityOfAssets)
                        : null,
                // Keeps the CSV in step with the grid's Standard filter
                // (MMG-4352); empty means every standard.
                standards: standardIDsOf(standard),
                // Translations still flow through t(); capWords() title-cases
                // each word so headers read consistently. Already-cased letters
                // and acronyms (e.g. SAP) are left untouched.
                columnHeaders: [
                    capWords(t('user:name')),
                    capWords(t('facility:facilityForm.sapFacilityNumber')),
                    capWords(t('manageFacility:quantityOfAssets')),
                    capWords(t('common:country')),
                    capWords(t('user:address')),
                    'Facility ID'
                ],
                settings: { valueOverrides: { country: countryMap } }
            },
            filename: 'facilities.csv'
        };
    };

    const hideMergeFacilitiesModal = () => {
        setState({ ...state, showMergeFacilityModal: false });
    };

    const hideAssignStandardModal = () => {
        setState({ ...state, showAssignStandardModal: false });
    };

    /*
     * Bulk-assign one standard to every selected facility (MMG-4353). The
     * selected rows only live in redux once saveFacility() has run, and the
     * grid rows carry the old standardID, so refresh from the server afterwards
     * rather than patching state.
     */
    const assignStandard = async (
        facilityIDs: string[],
        standardID: string
    ) => {
        // Balanced by hand around this one call: refreshData() below runs its own
        // begin/end pair, so holding this one open across it would leave the
        // counter stuck above zero and the page spinning.
        props.beginAjaxCall();

        try {
            const count = await assignStandardAPI(facilityIDs, standardID);
            props.endAjaxCall();

            setState({
                ...state,
                selectAll: false,
                selection: [],
                showAssignStandardModal: false
            });

            toastr.success(
                t('common:success'),
                t('assignStandardSuccess', { count }),
                constants.toastrSuccess
            );

            // Reloads the grid so the rows show the standard they now carry.
            refreshData();
        } catch (error) {
            props.endAjaxCall();
            constants.handleError(error, 'assign standard');
        }
    };

    const mergeFacilities = async (primary: string, secondary: string[]) => {
        const response = await mergeFacilitiesAPI(primary, secondary);
        if (!response) {
            toastr.warning(
                t('Error'),
                t('Failed to merge Facilities'),
                constants.toastrError
            );
            return;
        }

        // Remove the now merged facilities from redux if they're there
        await dispatch({
            type: types.MERGE_FACILITIES_SUCCESS,
            primaryId: primary,
            secondaryIds: secondary
        });

        // Remove the now merged facilities from state
        const updatedTableData = state.tableData.filter(tempFacility => {
            if (secondary.includes(tempFacility.id)) {
                return false;
            }

            return true;
        });

        setState({
            ...state,
            selectAll: false,
            selection: [],
            showMergeFacilityModal: false,
            tableData: updatedTableData
        });

        toastr.success(
            t('Success'),
            t('Facilities successfully merged'),
            constants.toastrSuccess
        );

        // Refresh data from server to get updated asset counts
        refreshData();
    };

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const searchForFacility = (facilityID: string) => {};

    React.useEffect(() => {
        props.beginAjaxCall();
        props.searchFacilitiesForAsyncSelect(
            undefined,
            usersCountries,
            null,
            () => {
                return;
            },
            saveFacilities,
            props.tableFilters.page,
            (_data: any) => {
                setData(_data);
            }
        );
        // eslint-disable-next-line
    }, [usersCountries]);

    const setForm = (form: FormGroup | FormArray) => {
        form.valueChanges.subscribe(
            (formValue: {
                country: Ioption;
                facilityName: string;
                quantityOfAssets: number;
                standard: Ioption[] | null;
            }) => {
                setFormValues(formValue);
                if (
                    formValue.facilityName === undefined ||
                    formValue.facilityName.length === 0
                ) {
                    debouncedSearch(
                        undefined,
                        formValue.country?.value,
                        formValue.quantityOfAssets,
                        props.tableFilters.page,
                        standardIDsOf(formValue.standard)
                    );
                    return;
                } else if (formValue.facilityName.length >= 3) {
                    debouncedSearch(
                        formValue.facilityName,
                        formValue.country?.value,
                        formValue.quantityOfAssets,
                        props.tableFilters.page,
                        standardIDsOf(formValue.standard)
                    );
                    return;
                }
            }
        );
    };

    return (
        <div className={`manage-facility`}>
            <Banner
                title={t('manageFacility:bannerTitle')}
                img={state.currentTile.srcBanner}
                color={state.currentTile.color}
                subtitle={props.selectedFacility?.name}
            />
            <div
                style={{
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                }}
                className="form-group form-group-sm"
            >
                {config && (
                    <FormGenerator onMount={setForm} fieldConfig={config} />
                )}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '8px',
                        paddingTop: '12px'
                    }}
                >
                    <AddButton
                        onClick={addFacility}
                        label={props.t('facility:newFacility')}
                    />
                    <ExportToExcelButton
                        wrapInCol={false}
                        getExportRequest={buildExportRequest}
                    />
                </div>
            </div>

            {state.selection.length >= 1 && (
                <DropdownButton
                    bsStyle={colorButton}
                    style={{
                        marginTop: '10px',
                        marginBottom: '10px',
                        marginLeft: '30px'
                    }}
                    title={t('manageLeads:bulkActions')}
                    id="table-action-button"
                    onSelect={(eventKey: any) => {
                        switch (eventKey) {
                            case 1:
                                if (state.selection.length >= 2) {
                                    saveFacility();
                                    setState({
                                        ...state,
                                        showMergeFacilityModal: true
                                    });
                                }
                                break;
                            case 2:
                                saveFacility();
                                setState({
                                    ...state,
                                    showAssignStandardModal: true
                                });
                                break;
                        }
                    }}
                >
                    {/* Merging needs a primary and at least one secondary;
                        assigning a standard works on a single row too. */}
                    {state.selection.length >= 2 && (
                        <MenuItem eventKey={1}>{t('mergeFacilities')}</MenuItem>
                    )}
                    <MenuItem eventKey={2}>{t('assignStandard')}</MenuItem>
                </DropdownButton>
            )}

            <SelectTable
                data={data.result}
                manual
                columns={columns}
                getTdProps={getTdProps}
                defaultPageSize={100}
                onPageSizeChange={onPageSizeChange}
                showPageSizeOptions={false}
                className={`beacon-table -highlight ${state.currentTile.color}`}
                previousText={t('common:previous')}
                nextText={t('common:next')}
                onPageChange={onPageChange}
                sortable={false}
                multiSort={false}
                noDataText={t('common:noDataSearch')}
                resizable={false}
                toggleSelection={toggleSelection}
                selectType="checkbox"
                toggleAll={toggleAll}
                isSelected={isSelected}
                ref={r => (state.checkboxTable = r)}
                keyField="id"
                loading={data.result.length === 0 && props.loading}
                page={props.tableFilters.page}
                pages={data.pages}
            />

            <EditFacilityModal
                modalClass=""
                colorButton={colorButton}
                selectedFacility={state.selectedFacility}
                disabled={false}
                approveMode={false}
                searchForFacility={searchForFacility}
                refreshTable={refreshData}
            />

            <FacilityContractModal
                colorButton={colorButton}
                secondWideModal={true}
            />
            <FacilityContactModal
                colorButton={colorButton}
                secondWideModal={true}
                hideTable={true}
                facilityID={state.selectedFacility.id}
            />

            <MergeFacilitiesModal
                showModal={state.showMergeFacilityModal}
                hideModal={hideMergeFacilitiesModal}
                selection={state.selection}
                colorButton={colorButton}
                mergeFacilities={mergeFacilities}
            />

            <AssignStandardModal
                showModal={state.showAssignStandardModal}
                hideModal={hideAssignStandardModal}
                selection={state.selection}
                colorButton={colorButton}
                assignStandard={assignStandard}
            />
        </div>
    );
};

const mapStateToProps = (state: IinitialState) => {
    const selectedFacility: Ifacility = initialFacility;

    const filteredFacilities = selectFilteredFacilities(
        state.facilities,
        state.manageFacility.tableFilters
    );

    return {
        tableFilters: selectManageFacilityTableFilters(state),
        facilities: state.facilities,
        selectedFacility,
        filteredFacilities,
        loading: selectIsLoading(state)
    };
};

export default withTranslation('manageFacility')(
    connect(mapStateToProps, {
        getFacilitiesByCountry,
        setTableFilter,
        setSelectedFacilityID,
        toggleEditFacilityModal,
        searchFacilitiesForAsyncSelect,
        beginAjaxCall,
        endAjaxCall
    })(ManageFacility)
);
